import React, { Component } from 'react';
import './App.css';
import { API, graphqlOperation, Auth } from 'aws-amplify';
import * as queries from './graphql/queries';
import * as mutations from './graphql/mutations';
import Subtask from './components/subtask'
import Header from './components/header'
import TaskRow from './components/taskRow'
import Timeline from './components/timeline'
import { ProgressRing, progressOf, sumUnits, rollUpUnits } from './components/progress'
import { applyOrder, sortableFor } from './components/order'
import { nestTask } from './components/nest'
import { Router, Switch, Route } from "react-router-dom";
import { createBrowserHistory } from 'history';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck, faTrash, faUndo, faReply, faPlus, faSearch, faTimes,
  faInbox, faCheckDouble, faRecycle, faCircleNotch,
} from '@fortawesome/free-solid-svg-icons'

const history = createBrowserHistory();

class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      newItem: '',
      creating: false,
      allTasks: [],
      query: '',
      removing: {},
      drag: null,
      nested: {},
      currentUser: props.authData || ''
    };

    this.handleChange = this.handleChange.bind(this);
    this.createTask = this.createTask.bind(this);
    this.modifyPomodoro = this.modifyPomodoro.bind(this);
  }

  componentDidMount = async() => {
    Auth.currentAuthenticatedUser().then(u => {this.setState({currentUser: u})});
    document.addEventListener('keydown', this.handleShortcut);
    await this.refreshAllTasks()
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleShortcut);
  }

  // "/" focuses search, "n" focuses the new-task field (when not already typing).
  handleShortcut = (event) => {
    const tag = (event.target.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || event.target.isContentEditable) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (event.key === '/' && this.searchInput) {
      event.preventDefault();
      this.searchInput.focus();
    } else if (event.key === 'n' && this.newTaskInput) {
      event.preventDefault();
      this.newTaskInput.focus();
    }
  }

  signOut = async () => {
    try {
      await Auth.signOut();
    } catch (err) {
      console.log(err);
    }
    if (this.props.onStateChange) this.props.onStateChange('signIn', null);
  }

  // Update optimistically, save, then refetch so the list reflects the server.
  toggleComplete = async (task) => {
    task.state = task.state === 'completed' ? null : 'completed';
    this.forceUpdate();
    let {tableData, owner, ...graphqlUpdatedTask} = task;
    await API.graphql(graphqlOperation(mutations.updateTask, {input: graphqlUpdatedTask}));
    await this.refreshAllTasks()
  }

  toggleTrash = async (task) => {
    task.deletionState = task.deletionState !== 'deleted' ? 'deleted' : null;
    this.forceUpdate();
    let {tableData, owner, ...graphqlUpdatedTask} = task;
    await API.graphql(graphqlOperation(mutations.updateTask, {input: graphqlUpdatedTask}));
    await this.refreshAllTasks()
  }

  deletePermanently = async (task) => {
    // Hide the row right away; the recursive delete can take a moment.
    this.setState({removing: {...this.state.removing, [task.id]: true}})
    const child_tasks = await API.graphql(graphqlOperation(queries.listTasks, {filter: {parentTaskId: {eq: task.id}}, limit: 999999999}));
    await Promise.all(child_tasks.data.listTasks.items.map(child => this.subTaskDelete(child)))
    await API.graphql(graphqlOperation(mutations.deleteTask, {input: {id: task.id }}));
    await this.refreshAllTasks()
  }

  subTaskDelete = async(task) => {
    const child_tasks = await API.graphql(graphqlOperation(queries.listTasks, {filter: {parentTaskId: {eq: task.id}}, limit: 999999999}));
    await Promise.all(child_tasks.data.listTasks.items.map(subtask => {
      return this.subTaskDelete(subtask)
    }))
    await API.graphql(graphqlOperation(mutations.deleteTask, {input: {id: task.id }}));
  }

  // Hide the row right away, then reload. Bumping `nested` remounts the target's open
  // panel so it fetches its new subtask.
  nestUnder = async (task, target) => {
    this.setState({removing: {...this.state.removing, [task.id]: true}})
    try {
      await nestTask(task, target)
      this.setState(state => ({nested: {...state.nested, [target.id]: (state.nested[target.id] || 0) + 1}}))
      await this.refreshAllTasks()
    } finally {
      this.setState(state => {
        const removing = {...state.removing}
        delete removing[task.id]
        return {removing}
      })
    }
  }

  handleChange(event) {
    this.setState({[event.target.name]: event.target.value});
  }

  setPomodoro = (addPomodoro) => {
    this.forceUpdate()
  }

  modifyPomodoro = (doneDiff,estimateDiff) => {
    this.forceUpdate()
  }

  childChanged = () => {
    this.forceUpdate()
  }

  createTask = async (event) => {
    if (event) event.preventDefault();
    const name = this.state.newItem.trim();
    if (!name || this.state.creating) return;
    this.setState({creating: true});
    var randomColor = require('randomcolor')
    var color = randomColor();
    const todoDetails = {
      name: name,
      description: 'Learn AWS AppSync',
      color: color,
      pomodoros: 0,
      estimatedPomodoro: 1,
      state: 'new',
      deletionState: 'false',
      deadline: 'none',
      parentTaskId: 'none'
    };
    try {
      await API.graphql(graphqlOperation(mutations.createTask, {input: todoDetails}));
      await this.refreshAllTasks()
      this.setState({newItem: ''})
    } finally {
      this.setState({creating: false})
    }
  }

  refreshAllTasks = async () => {
    // Only apply the newest response, so a slow earlier fetch can't overwrite newer data.
    const request = this.latestRefresh = (this.latestRefresh || 0) + 1;
    // Fetch subtasks too (every page), so top-level totals can be summed from them.
    let items = [];
    let nextToken = null;
    do {
      const page = await API.graphql(graphqlOperation(queries.listTasks, {limit: 999999999, nextToken}));
      items = items.concat(page.data.listTasks.items);
      nextToken = page.data.listTasks.nextToken;
    } while (nextToken);
    const topLevel = rollUpUnits(items).filter(task => task.parentTaskId === 'none');
    if (request === this.latestRefresh) {
      this.setState({allTasks: {data: {listTasks: {items: topLevel}}}})
    }
  }

  updateTaskName = async (rowData, name) => {
    var currentTask = rowData
    currentTask.name = name
    this.forceUpdate()
    let {tableData, owner, ...graphqlUpdatedTask} = currentTask;
    await API.graphql(graphqlOperation(mutations.updateTask, {input: graphqlUpdatedTask}));
    await this.refreshAllTasks()
  }

  checkIfNotCompleted = (item) => {
    return (item.state !== 'completed' && item.deletionState !== 'deleted')
  }

  checkIfCompleted = (item) => {
    return (item.state === 'completed' && item.deletionState !== 'deleted')
  }

  checkIfDeleted = (item) => {
    return (item.deletionState === 'deleted')
  }

  isLoaded = () => this.state.allTasks.data !== undefined

  tasksWhere = (predicate) => {
    if (!this.isLoaded()) {
      return []
    }
    return this.state.allTasks.data.listTasks.items.filter(task => !this.state.removing[task.id] && predicate(task))
  }

  matchesQuery = (task) => {
    const query = this.state.query.trim().toLowerCase();
    return !query || (task.name || '').toLowerCase().indexOf(query) !== -1;
  }

  // ---------- Rendering ----------

  renderDetail = (allowEdit) => (rowData) => (
    <Subtask
      key={rowData.id + ':' + (this.state.nested[rowData.id] || 0)}
      data={rowData}
      allowEdit={allowEdit}
      modifyPomodoro={this.modifyPomodoro}
      setPomodoro={this.setPomodoro}
      onChange={this.childChanged}
    />
  )

  renderToolbar = (label, count) => (
    <div className="list-toolbar">
      <div className="list-toolbar__label eyebrow">
        {label} <span className="list-toolbar__count">{count}</span>
      </div>
      <label className="search">
        <FontAwesomeIcon icon={faSearch} className="search__icon" />
        <input
          ref={(el) => { this.searchInput = el; }}
          className="search__input"
          type="search"
          name="query"
          value={this.state.query}
          onChange={this.handleChange}
          onKeyDown={(e) => { if (e.key === 'Escape') { this.setState({query: ''}); e.target.blur(); } }}
          placeholder="Search tasks"
          aria-label="Search tasks"
        />
        {this.state.query
          ? <button className="search__clear" onClick={() => this.setState({query: ''})} aria-label="Clear search"><FontAwesomeIcon icon={faTimes} /></button>
          : <kbd className="kbd">/</kbd>}
      </label>
    </div>
  )

  renderList = (tasks, { actions, allowEdit, empty }) => {
    if (!this.isLoaded()) {
      return (
        <div className="task-list" aria-busy="true">
          {[0, 1, 2].map(i => <div key={i} className="skeleton skeleton--row" style={{ animationDelay: (i * 120) + 'ms' }} />)}
        </div>
      )
    }
    const visible = applyOrder('root', tasks.filter(this.matchesQuery));
    if (visible.length === 0) {
      const searching = tasks.length > 0;
      return (
        <div className="empty">
          <div className="empty__icon"><FontAwesomeIcon icon={searching ? faSearch : empty.icon} /></div>
          <div className="empty__title">{searching ? 'No matches' : empty.title}</div>
          <p className="empty__text">{searching ? 'Nothing here matches “' + this.state.query.trim() + '”.' : empty.text}</p>
        </div>
      )
    }
    return (
      <div className="task-list">
        {visible.map(task => (
          <TaskRow
            key={task.id}
            task={task}
            allowEdit={allowEdit}
            onRename={allowEdit ? this.updateTaskName : null}
            actions={actions}
            renderPanel={this.renderDetail(allowEdit)}
            sortable={allowEdit ? sortableFor(this, 'root', visible, task, this.nestUnder) : null}
          />
        ))}
      </div>
    )
  }

  renderComposer = () => (
    <form className={'composer' + (this.state.creating ? ' is-busy' : '')} onSubmit={this.createTask}>
      <span className="composer__icon" aria-hidden="true">
        <FontAwesomeIcon icon={this.state.creating ? faCircleNotch : faPlus} spin={this.state.creating} />
      </span>
      <input
        ref={(el) => { this.newTaskInput = el; }}
        className="composer__input"
        type="text"
        name="newItem"
        value={this.state.newItem}
        onChange={this.handleChange}
        placeholder="Add a new task…"
        aria-label="New task name"
        autoComplete="off"
      />
      {!this.state.newItem && <kbd className="kbd composer__kbd">N</kbd>}
      <button type="submit" className="btn btn--primary" disabled={!this.state.newItem.trim() || this.state.creating}>
        {this.state.creating ? 'Adding…' : 'Add task'}
      </button>
    </form>
  )

  renderOverview = (current, completed) => {
    const totals = sumUnits(current);
    const pct = totals.estimate > 0 ? Math.round(100 * totals.done / totals.estimate) : 0;
    const closest = current
      .filter(t => progressOf(t).estimate > 0)
      .sort((a, b) => progressOf(b).pct - progressOf(a).pct)
      .slice(0, 3);

    return (
      <div className="overview-wrap">
        <div className="window overview">
          <div className="window__bar">
            <span className="window__dots"><i /><i /><i /></span>
            <span className="window__path">progresstodo / overview</span>
          </div>
          <div className="overview__body">
            <div className="overview__ring">
              <ProgressRing pct={pct} />
              <div className="overview__ring-label">
                <strong>{this.isLoaded() ? pct + '%' : '—'}</strong>
                <span>overall</span>
              </div>
            </div>
            <dl className="overview__stats">
              <div><dt>Units done</dt><dd>{totals.done}</dd></div>
              <div><dt>Units left</dt><dd>{Math.max(totals.estimate - totals.done, 0)}</dd></div>
              <div><dt>Finished</dt><dd>{completed.length}</dd></div>
            </dl>
          </div>
          {closest.length > 0 && (
            <div className="overview__closest">
              <span className="eyebrow">Closest to done</span>
              {closest.map(task => {
                const p = progressOf(task);
                return (
                  <div key={task.id} className="mini" style={{ '--task-color': task.color }}>
                    <span className="mini__dot" />
                    <span className="mini__name">{task.name}</span>
                    <span className="mini__bar"><span style={{ width: Math.min(p.pct, 100) + '%' }} /></span>
                    <span className="mini__pct">{p.pct}%</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="float-chip"><span className="pulse-dot" /> Progress rolls up from every subtask</div>
      </div>
    )
  }

  renderHome = () => {
    const current = this.tasksWhere(this.checkIfNotCompleted);
    const completed = this.tasksWhere(this.checkIfCompleted);
    const actions = [
      { key: 'complete', icon: faCheck, label: 'Mark done', tone: 'success', onClick: this.toggleComplete },
      { key: 'trash', icon: faTrash, label: 'Move to trash', tone: 'danger', onClick: this.toggleTrash },
    ];
    return (
      <main className="page">
        <section className="hero container">
          <div className="hero__copy">
            <div className="pill">
              <span className="pulse-dot" />
              {this.isLoaded() ? current.length + ' in progress · ' + completed.length + ' done · synced' : 'Syncing your tasks'}
            </div>
            <h1 className="display">
              Big work,<br />
              <span className="gradient-text">piece by piece<span className="gradient-dot">.</span></span>
            </h1>
            <p className="lede">
              Break a task into subtasks, estimate each one in units, and watch the progress
              fill in as you go. Every change saves to your account automatically.
            </p>
            <ul className="checklist">
              <li><FontAwesomeIcon icon={faCheck} /> Nest subtasks as deep as you need</li>
              <li><FontAwesomeIcon icon={faCheck} /> Totals roll up to the parent task</li>
              <li><FontAwesomeIcon icon={faCheck} /> Rich notes on every task</li>
            </ul>
          </div>
          {this.renderOverview(current, completed)}
        </section>

        <section className="container section">
          {this.renderComposer()}
          {this.renderToolbar('In progress', current.length)}
          {this.renderList(current, {
            actions,
            allowEdit: true,
            empty: { icon: faInbox, title: 'A clean slate', text: 'Add your first task above. Press N from anywhere to jump straight to it.' },
          })}
        </section>
      </main>
    )
  }

  renderPageHero = (eyebrow, title, accent, text) => (
    <section className="hero hero--compact container">
      <div className="hero__copy">
        <div className="pill"><span className="pulse-dot" />{eyebrow}</div>
        <h1 className="display display--sm">
          {title} <span className="gradient-text">{accent}<span className="gradient-dot">.</span></span>
        </h1>
        <p className="lede">{text}</p>
      </div>
    </section>
  )

  renderCompleted = () => {
    const completed = this.tasksWhere(this.checkIfCompleted);
    const actions = [
      { key: 'undo', icon: faUndo, label: 'Mark not done', onClick: this.toggleComplete },
      { key: 'trash', icon: faTrash, label: 'Move to trash', tone: 'danger', onClick: this.toggleTrash },
    ];
    return (
      <main className="page">
        {this.renderPageHero(
          this.isLoaded() ? completed.length + ' finished' : 'Loading',
          'Completed,', 'nicely done',
          'Everything you have wrapped up. Undo a task to move it back into progress.'
        )}
        <section className="container section">
          {this.isLoaded() && <Timeline tasks={completed} onUndo={this.toggleComplete} />}
          {this.renderToolbar('Completed', completed.length)}
          {this.renderList(completed, {
            actions,
            allowEdit: false,
            empty: { icon: faCheckDouble, title: 'Nothing finished yet', text: 'Tasks you mark done will land here.' },
          })}
        </section>
      </main>
    )
  }

  renderTrash = () => {
    const deleted = this.tasksWhere(this.checkIfDeleted);
    const actions = [
      { key: 'restore', icon: faReply, label: 'Restore', onClick: this.toggleTrash },
      { key: 'delete', icon: faTrash, label: 'Delete permanently', tone: 'danger', confirm: true, onClick: this.deletePermanently },
    ];
    return (
      <main className="page">
        {this.renderPageHero(
          this.isLoaded() ? deleted.length + ' in trash' : 'Loading',
          'Trash,', 'for second thoughts',
          'Restore anything you did not mean to toss. Deleting permanently also removes every subtask.'
        )}
        <section className="container section">
          {this.renderToolbar('Trash', deleted.length)}
          {this.renderList(deleted, {
            actions,
            allowEdit: false,
            empty: { icon: faRecycle, title: 'Trash is empty', text: 'Tasks you move to the trash show up here.' },
          })}
        </section>
      </main>
    )
  }

  render() {
    const counts = {
      current: this.tasksWhere(this.checkIfNotCompleted).length,
      completed: this.tasksWhere(this.checkIfCompleted).length,
      deleted: this.tasksWhere(this.checkIfDeleted).length,
    };
    return (
      <Router history={history}>
        <div className="app">
          <div className="backdrop" aria-hidden="true" />
          <Header counts={counts} currentUser={this.state.currentUser} onSignOut={this.signOut} />
          <Switch>
            <Route path="/completed" exact render={this.renderCompleted} />
            <Route path="/deleted" exact render={this.renderTrash} />
            <Route path="/" render={this.renderHome} />
          </Switch>
          <footer className="footer container">
            <span>ProgressTodo</span>
            <span className="footer__keys"><kbd className="kbd">N</kbd> new task <kbd className="kbd">/</kbd> search</span>
          </footer>
        </div>
      </Router>
    );
  }
}

export default App;
